import { SearchedRouteElement } from 'src/app/models/searched-route-element';

// Half the side of the box given to synced entries that arrive without one (searched in Android Auto).
const SYNCED_BOUNDINGBOX_HALF_DEG = 0.005;
const MAX_LAST_SEARCHES = 5;

/** `ts` (ms) orders the list when it is merged with the Android app's; older entries lack it. */
export type StoredSearch = SearchedRouteElement & { ts?: number };

export interface TextSegment {
  text: string;
  highlighted: boolean;
}

/** Splits a Places suggestion's text into plain and highlighted (matched) runs. */
export function getTextSegments(searchEl: any): TextSegment[] {
  const formattable = searchEl?.placePrediction?.text;
  if (!formattable?.text) {
    return [];
  }
  const text: string = formattable.text;
  const matches = formattable.matches || [];
  if (!matches.length) {
    return [{ text, highlighted: false }];
  }
  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const m of matches) {
    const start = m.startOffset ?? 0;
    const end = m.endOffset ?? start;
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), highlighted: false });
    }
    if (end > start) {
      segments.push({ text: text.slice(start, end), highlighted: true });
    }
    cursor = end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlighted: false });
  }
  return segments;
}

/** The history after selecting `selected`: older entries for the same place dropped, newest first, at most 5. */
export function storeLastSearch(lastSearches: StoredSearch[], selected: SearchedRouteElement, now: number): StoredSearch[] {
  // Remove any previous occurrence of the same location (by lat/lon)
  let result = lastSearches.filter(
    s => !(s.lat === selected.lat && s.lon === selected.lon)
  );
  result.unshift({ ...selected, ts: now });
  if (result.length > MAX_LAST_SEARCHES) {
    result = result.slice(0, MAX_LAST_SEARCHES);
  }
  return result;
}

/**
 * Parses the merged history returned by the Android app. Returns null when there is nothing usable
 * (no answer, invalid JSON, not an array); otherwise drops invalid entries and gives entries without
 * a 4-number bounding box a small one around their position.
 */
export function syncSearchHistory(mergedJson: string | null | undefined): StoredSearch[] | null {
  if (!mergedJson) {
    return null;
  }
  let merged: StoredSearch[];
  try {
    merged = JSON.parse(mergedJson);
  } catch {
    // Malformed JSON from the app is treated the same as no answer.
    return null;
  }
  if (!Array.isArray(merged)) {
    return null;
  }
  return merged
    .filter(s => s?.display_name && typeof s.lat === 'number' && typeof s.lon === 'number')
    .map(s => s.boundingbox?.length === 4 ? s : {
      ...s,
      boundingbox: [
        s.lat + SYNCED_BOUNDINGBOX_HALF_DEG, s.lon + SYNCED_BOUNDINGBOX_HALF_DEG,
        s.lat - SYNCED_BOUNDINGBOX_HALF_DEG, s.lon - SYNCED_BOUNDINGBOX_HALF_DEG
      ]
    });
}
